
const schema = (formData = {}) => {
  const showStats = !!formData.useStatistics;

  const fieldsets = [
    {
      id: 'default',
      title: 'Default',
      fields: ['preheaderText', 'headerText', 'descriptionText', 'headerAlignment', 'blockAlignment', 'columns', 'imagePosition', 'cardTextAlignment', 'blockBackgroundColor', 'useStatistics'],
    },
    {
      id: 'mobile',
      title: 'Mobile carousel',
      fields: ['mobileCarousel', 'mobileCarouselAutoplay', 'mobileCarouselDots', 'carouselControlsColor'],
    },
  ];

  // Add styling options only when image is not background
  const showStyling = formData.imagePosition && formData.imagePosition !== 'background';
  if (showStyling && !showStats) {
    fieldsets.push({
      id: 'styling',
      title: 'Card Styling',
      fields: ['cardBackgroundColor'],
    });
  }

  if (showStats) {
    fieldsets.push({
      id: 'statistics',
      title: 'Statistics',
      fields: ['cardBackgroundColor', 'statsFormatK', 'statAnimationMs', 'statistics'],
    });
  } else {
    fieldsets.push({
      id: 'cards',
      title: 'Cards',
      fields: ['items'],
    });
  }

  return {
    title: 'Multi Card Block',
    fieldsets,
    properties: {
      preheaderText: {
        title: 'Preheader',
        type: 'string',
        description: 'Optional text that appears above the main header.',
      },
      headerText: {
        title: 'Header',
        type: 'string',
        widget: 'textarea',
        description: 'Main heading text for the block. Supports HTML tags like <strong>bold</strong>, <em>italic</em>, and line breaks. This text is used for TOC navigation.',
      },
      descriptionText: {
        title: 'Description',
        type: 'string',
        widget: 'textarea',
        description: 'Description text that appears below the header.',
      },
      headerAlignment: {
        title: 'Header alignment',
        choices: [
          ['left', 'Left'],
          ['center', 'Center'],
          ['right', 'Right'],
        ],
        type: 'string',
        default: 'left',
        description: 'Alignment for the header section (preheader, header, description).',
      },
      blockAlignment: {
        title: 'Content alignment',
        choices: [
          ['left', 'Left'],
          ['center', 'Center'],
          ['right', 'Right'],
        ],
        type: 'string',
        default: 'left',
        description: 'Alignment for the cards or statistics content.',
      },
      columns: {
        title: 'Number of columns (desktop)',
        type: 'number',
        minimum: 1,
        maximum: 6,
        default: 3,
      },
      imagePosition: {
        title: 'Image position',
        choices: [
          ['background', 'Background (default)'],
          ['above-card', 'Above card'],
          ['above-content', 'Above content (below title)'],
          ['below-content', 'Below content (above button)'],
          ['below-card', 'Below card'],
        ],
        type: 'string',
        default: 'background',
        description: 'How to display card images. Applies to all cards.',
      },
      cardTextAlignment: {
        title: 'Card text vertical alignment',
        choices: [
          ['top', 'Top'],
          ['middle', 'Middle'],
          ['bottom', 'Bottom'],
        ],
        type: 'string',
        default: 'bottom',
        description: 'Vertical alignment of text within cards.',
      },
      blockBackgroundColor: {
        title: 'Block background color',
        choices: [
          ['none', 'None (transparent)'],
          ['#fff', 'White'],
          ['var(--blue)', 'Blue'],
          ['var(--darkblue)', 'Dark Blue'],
          ['var(--lightblue)', 'Light Blue'],
          ['var(--yellow)', 'Yellow'],
          ['var(--peach)', 'Peach'],
        ],
        type: 'string',
        default: 'none',
        description: 'Background color for the entire block section.',
      },
      // DESIGNER: Customize the color options below to match your theme
      // Format: ['css-value|theme', 'Display Name']
      // theme = 'light' (uses dark text) or 'dark' (uses light text)
      // This field is used for both cards (when image is not background) and statistics
      cardBackgroundColor: {
        title: 'Background color',
        choices: [
          ['none', 'None (transparent)'],
          ['#fff|light', 'White'],
          ['var(--blue)|dark', 'Blue'],
          ['var(--darkblue)|dark', 'Dark Blue'],
          ['var(--lightblue)|light', 'Light Blue'],
          ['var(--yellow)|light', 'Yellow'],
          ['var(--peach)|light', 'Peach'],
        ],
        type: 'string',
        default: 'var(--lightgrey)|light',
        description: 'Background color for cards or statistics. Text color (light/dark) is determined automatically from the theme indicator in the color value.',
      },
      useStatistics: {
        title: 'Use statistics instead of cards',
        type: 'boolean',
        default: false,
        description: 'If enabled, cards are hidden (not deleted) and a statistics list is shown instead.',
      },
      statsFormatK: {
        title: 'Compact thousands into k for all stats',
        type: 'boolean',
        default: false,
        description: 'When enabled and value ≥ 1000, animate in 1000-steps and display as “xk”.',
      },
      statAnimationMs: {
        title: 'Animation duration (ms) for all stats',
        type: 'number',
        default: 6000,
        description: 'How long the number animation should take, in milliseconds (e.g., 6000 = 6 seconds).',
        minimum: 200,
        maximum: 60000,
      },
      mobileCarousel: {
        title: 'Use carousel on mobile',
        type: 'boolean',
        default: false,
        description: 'On ≤ 768px, items/statistics become a swipeable carousel.',
      },
      mobileCarouselAutoplay: {
        title: 'Autoplay (mobile)',
        type: 'boolean',
        default: true,
      },
      mobileCarouselDots: {
        title: 'Show dots (mobile)',
        type: 'boolean',
        default: true,
      },
      carouselControlsColor: {
        title: 'Carousel controls color',
        choices: [
          ['light', 'Light (for dark backgrounds)'],
          ['dark', 'Dark (for light backgrounds)'],
        ],
        type: 'string',
        default: 'light',
        description: 'Color of carousel dots on mobile.',
      },
      items: {
        title: 'Cards',
        widget: showStats ? 'hidden' : 'object_list',
        default: [],
        schema: {
          title: 'Card',
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: ['preheader', 'title', 'description', 'linkText', 'url', 'anchor', 'buttonStyle', 'image'],
            },
          ],
          properties: {
            preheader: { 
              title: 'Preheader', 
              type: 'string',
              description: 'Optional text that appears above the title.',
            },
            title: { title: 'Title', type: 'string' },
            description: { 
              title: 'Introduction', 
              widget: 'slate',
              description: 'Card introduction text with full rich text editing support.',
            },
            linkText: { title: 'Link text', type: 'string' },
            url: {
              title: 'Link URL',
              widget: 'object_browser',
              mode: 'link',
              allowExternals: true,
            },
            anchor: {
              title: 'Anchor (optional)',
              type: 'string',
              description: 'Appends to link. If no link is set, this becomes the target.',
            },
            buttonStyle: {
              title: 'Button style',
              choices: [
                ['primary', 'Primary'],
                ['secondary', 'Secondary'],
                ['tertiary', 'Tertiary'],
                ['ghost', 'Ghost'],
              ],
              type: 'string',
              default: 'primary',
              description: 'Visual style for the button.',
            },
            image: {
              title: 'Background image',
              widget: 'image',
            },
          },
          required: [],
        },
      },
      statistics: {
        title: 'Statistics',
        widget: showStats ? 'object_list' : 'hidden',
        default: [],
        schema: {
          title: 'Statistic',
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: ['label', 'value', 'isPercentage', 'suffix', 'extraInfo', 'url', 'anchor'],
            },
          ],
          properties: {
            label: { title: 'Label', type: 'string' },
            value: {
              title: 'Value (number)',
              type: 'number',
              description: 'The number to display. Supports decimals (e.g., 4.04).',
            },
            isPercentage: {
              title: 'Display as percentage',
              type: 'boolean',
              default: false,
              description: 'When enabled, automatically adds % symbol and formats as percentage.',
            },
            suffix: { 
              title: 'Suffix (e.g. + or M)', 
              type: 'string',
              description: 'Optional suffix. Not needed if "Display as percentage" is enabled.',
            },
            extraInfo: { title: 'Extra info', type: 'string' },
            url: {
              title: 'Link URL (optional)',
              widget: 'object_browser',
              mode: 'link',
              allowExternals: true,
            },
            anchor: {
              title: 'Anchor (optional)',
              type: 'string',
              description: 'Appends to link. If no link is set, this becomes the target.',
            },
          },
          required: ['value'],
        },
      },
    },
    required: [],
  };
};

export default schema;
